import React, { useEffect, useRef } from "react";
import { TriggeredHook } from "sonobello.utilities.react";

import { bodyMassIndex } from "../../types/Constants";
import { DisqualifyingReasonsType } from "../../types/DisqualifyingReason";
import { evaluatePatientDisqualification, type IPatient } from "../../types/Entities/Patient";
import { loadFromLocal, saveToLocal } from "../../types/LocalStorage";
import IMedicalQualification from "../../steps/Medical/types/IMedicalQualification";
import AllMedicalQuestionsEvaluator from "../../steps/Medical/Compositions/AllMedicalQuestionsEvaluator";
import {
  IContactPatientQualificationRequest,
  IUseContactPatientQualificationProps
} from "../hooks/useContactPatientQualification";

interface ContactPatientQualificationRecord {
  /** The id of the contact with which the qualification is associated. */
  contactId: string;
  /** The id of the patient whom has been assessed for qualification. */
  patientId: string;
  /** The results of the qualification assessment. */
  payload: IContactPatientQualificationRequest;
}

interface IContactPatientQualificationCreatorProps {
  /** The id of the contact associated with the qualification request. */
  contactId: string;
  /** The current state of the medical questionnaire. */
  medicalForm: IMedicalQualification;
  /** The current state of the session patient. */
  patient: IPatient;
  /** The hook used to post patient qualification results. */
  useContactPatientQualification: TriggeredHook<
    IContactPatientQualificationRequest,
    IContactPatientQualificationRequest,
    boolean,
    IUseContactPatientQualificationProps
  >;
}

export type { IContactPatientQualificationCreatorProps };

const localStorageKey = "qualificationRecord";

const areContactPatientQualificationRecordsEqual = (
  a: ContactPatientQualificationRecord,
  b: ContactPatientQualificationRecord
): boolean => {
  return (
    a.contactId === b.contactId &&
    a.patientId === b.patientId &&
    a.payload.dateOfBirth === b.payload.dateOfBirth &&
    a.payload.heightInches === b.payload.heightInches &&
    a.payload.weight === b.payload.weight &&
    a.payload.bodyMassIndex === b.payload.bodyMassIndex &&
    a.payload.isMedicallyQualified === b.payload.isMedicallyQualified
  );
};

const buildQualificationRecord = (
  contactId: string,
  patientId: string,
  patient: IPatient,
  medicalForm: IMedicalQualification
): ContactPatientQualificationRecord | null => {
  let disqualifyingReason: DisqualifyingReasonsType | undefined;

  if (patient.height && patient.weight && patient.dateOfBirth) {
    disqualifyingReason = evaluatePatientDisqualification(patient as never);
  }

  if (!disqualifyingReason && !AllMedicalQuestionsEvaluator.isComplete(medicalForm)) {
    return null;
  }

  const qualificationPayload: IContactPatientQualificationRequest = {
    heightInches: undefined,
    weight: patient.weight || undefined,
    bodyMassIndex: undefined,
    dateOfBirth: patient.dateOfBirth || undefined,
    isMedicallyQualified: !disqualifyingReason && !AllMedicalQuestionsEvaluator.isNonCandidate(medicalForm)
  };

  if (patient.height) {
    qualificationPayload.heightInches = patient.height.feet * 12 + patient.height.inches;
  }

  if (qualificationPayload.heightInches && patient.weight) {
    qualificationPayload.bodyMassIndex = bodyMassIndex(patient.weight, qualificationPayload.heightInches);
  }

  return {
    contactId,
    patientId,
    payload: qualificationPayload
  };
};

/**
 * A props-driven composition component which decides when to dispatch a patient qualification record and persists the
 * latest dispatched state for dedupe behavior.
 */
const ContactPatientQualificationCreator: React.FC<IContactPatientQualificationCreatorProps> = ({
  contactId,
  medicalForm,
  patient,
  useContactPatientQualification: useContactPatientQualificationHook
}) => {
  const lastUpdatedQualificationRef = useRef(loadFromLocal<ContactPatientQualificationRecord>(localStorageKey));
  const lastDispatchedQualificationRef = useRef<ContactPatientQualificationRecord | undefined>(undefined);

  const { result, execute } = useContactPatientQualificationHook({
    contactId,
    patientId: patient.id
  });

  useEffect(() => {
    if (!patient?.id) return;

    const qualificationRecord = buildQualificationRecord(contactId, patient.id, patient, medicalForm);
    if (!qualificationRecord) return;

    if (
      lastUpdatedQualificationRef.current &&
      areContactPatientQualificationRecordsEqual(lastUpdatedQualificationRef.current, qualificationRecord)
    ) {
      return;
    }

    lastDispatchedQualificationRef.current = qualificationRecord;
    execute(qualificationRecord.payload);
  }, [contactId, execute, medicalForm, patient]);

  useEffect(() => {
    if (!result) return;

    const updatedRecord = lastDispatchedQualificationRef.current
      ? {
          ...lastDispatchedQualificationRef.current,
          payload: result
        }
      : {
          contactId,
          patientId: patient.id,
          payload: result
        };

    if (
      lastUpdatedQualificationRef.current &&
      areContactPatientQualificationRecordsEqual(lastUpdatedQualificationRef.current, updatedRecord)
    ) {
      return;
    }

    lastUpdatedQualificationRef.current = updatedRecord;
    saveToLocal(updatedRecord, localStorageKey);
  }, [contactId, patient.id, result]);

  return <></>;
};

export default ContactPatientQualificationCreator;
